// Vendored from the JETCAM DXF Viewer (src/render/camera.ts), 2026-09-03.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Unmodified. The ../geometry and ../parse imports resolve to the shims
// beside this directory, which re-export the engine.

import { type Bounds } from "../geometry/index.ts";

/**
 * The camera: one world-to-screen affine transform, and nothing else.
 *
 * Everything drawn goes through `ctx.setTransform`, so draw calls pass world
 * coordinates straight through. Transforming points by hand in the draw loop
 * would be both slower and a standing invitation for the ruler, the hit test
 * and the renderer to disagree about where things are.
 *
 * Y is up. The transform's negative vertical scale is the *only* place that
 * flip happens.
 */
export interface Camera {
  /** Screen pixels per drawing unit. */
  readonly scale: number;
  /** World coordinates at the centre of the viewport. */
  readonly center: Point;
  /** Viewport size in CSS pixels. */
  readonly width: number;
  readonly height: number;
  /**
   * World point the ruler's displayed values (and only the ruler's displayed
   * values) are shown relative to — "Translate to origin" in the toolbar.
   * `{0,0}` (the default) shows the file's true coordinates.
   *
   * Display only, deliberately: rendering itself is always precise regardless
   * of this — see `affineOf`'s own doc comment for why — so this field is
   * free to be a pure cosmetic preference, matching JETCAM's own viewer,
   * rather than something correctness depends on.
   */
  readonly origin: Point;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** A 2D affine transform in the order `ctx.setTransform` expects. */
export interface Affine {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly e: number;
  readonly f: number;
}

/** Zoom range, wide enough for a watch part and a full nest sheet alike. */
const MIN_SCALE = 1e-6;
const MAX_SCALE = 1e7;
/** Fraction of the viewport left as margin by `fitBounds`. */
const FIT_PADDING = 0.06;
/** Scale used when fitting something with no size at all, e.g. a lone POINT. */
const DEGENERATE_FIT_SCALE = 10;

export function createCamera(width: number, height: number): Camera {
  return { scale: 1, center: { x: 0, y: 0 }, width, height, origin: { x: 0, y: 0 } };
}

export function clampScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 1;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export function resize(camera: Camera, width: number, height: number): Camera {
  return { ...camera, width, height };
}

/**
 * The world-to-screen transform, always expressed relative to `camera.center`
 * — deliberately unconditional, not tied to `camera.origin`'s toggle.
 *
 * `d` is negative because world Y runs up and screen Y runs down. `e`/`f` are
 * always exactly `width/2`/`height/2`: constants, never a function of how far
 * `center` is from 0,0. Every point handed to the canvas alongside this
 * matrix (`draw.ts`) has `center` subtracted first, in JS double precision,
 * so what actually reaches the canvas's own internal float32 rasteriser is
 * always small — bounded by roughly the current viewport's own world-space
 * size, never by the drawing's distance from the file's true origin. That
 * distance is what used to end up baked into `e`/`f` here, and is exactly
 * what starved the rasteriser of precision at high zoom on a part far from
 * 0,0 — this fix is why zooming in far no longer makes geometry disappear,
 * with no user action required, matching JETCAM's own viewer.
 */
export function affineOf(camera: Camera): Affine {
  return {
    a: camera.scale,
    b: 0,
    c: 0,
    d: -camera.scale,
    e: camera.width / 2,
    f: camera.height / 2,
  };
}

/**
 * Deliberately ignores `camera.origin`: this is plain JS double-precision
 * arithmetic, never touches the canvas, and has ample precision for these
 * magnitudes regardless of how far the geometry sits from 0,0. Everything
 * that positions itself through this function (the ruler, hit-testing,
 * tooltips, measurements) was never affected by the precision issue
 * `camera.origin` exists to work around — only `draw.ts`'s direct
 * `ctx.moveTo`/`lineTo`/`arc` calls under the raw world transform are.
 */
export function worldToScreen(camera: Camera, p: Point): Point {
  return {
    x: (p.x - camera.center.x) * camera.scale + camera.width / 2,
    y: camera.height / 2 - (p.y - camera.center.y) * camera.scale,
  };
}

export function screenToWorld(camera: Camera, p: Point): Point {
  return {
    x: camera.center.x + (p.x - camera.width / 2) / camera.scale,
    y: camera.center.y + (camera.height / 2 - p.y) / camera.scale,
  };
}

/** World-space rectangle currently visible. */
export function viewportBounds(camera: Camera): Bounds {
  const halfW = camera.width / 2 / camera.scale;
  const halfH = camera.height / 2 / camera.scale;
  return {
    min: { x: camera.center.x - halfW, y: camera.center.y - halfH, z: -Infinity },
    max: { x: camera.center.x + halfW, y: camera.center.y + halfH, z: Infinity },
  };
}

/**
 * Zoom by `factor` about a screen point, keeping whatever is under that point
 * exactly where it is. Zooming about the viewport centre instead is the single
 * most irritating thing a CAD viewer can do.
 */
export function zoomAt(camera: Camera, screenPoint: Point, factor: number): Camera {
  const scale = clampScale(camera.scale * factor);
  if (scale === camera.scale) return camera;

  const anchor = screenToWorld(camera, screenPoint);
  return {
    ...camera,
    scale,
    center: {
      x: anchor.x - (screenPoint.x - camera.width / 2) / scale,
      y: anchor.y + (screenPoint.y - camera.height / 2) / scale,
    },
  };
}

/** Pan by a screen-pixel delta, as produced by a drag. */
export function panByScreen(camera: Camera, dx: number, dy: number): Camera {
  return {
    ...camera,
    center: {
      x: camera.center.x - dx / camera.scale,
      y: camera.center.y + dy / camera.scale,
    },
  };
}

/**
 * Frame a world-space box. Degenerate boxes — a single point, or geometry that
 * is exactly a horizontal line — are handled rather than producing an infinite
 * or zero scale.
 */
export function fitBounds(camera: Camera, bounds: Bounds, padding = FIT_PADDING): Camera {
  const boxWidth = bounds.max.x - bounds.min.x;
  const boxHeight = bounds.max.y - bounds.min.y;
  const center = {
    x: (bounds.min.x + bounds.max.x) / 2,
    y: (bounds.min.y + bounds.max.y) / 2,
  };

  const usableW = camera.width * (1 - padding);
  const usableH = camera.height * (1 - padding);

  let scale: number;
  if (boxWidth <= 0 && boxHeight <= 0) {
    scale = DEGENERATE_FIT_SCALE;
  } else if (boxWidth <= 0) {
    scale = usableH / boxHeight;
  } else if (boxHeight <= 0) {
    scale = usableW / boxWidth;
  } else {
    scale = Math.min(usableW / boxWidth, usableH / boxHeight);
  }

  return { ...camera, scale: clampScale(scale), center };
}

/** Convert a screen-pixel distance into drawing units at the current zoom. */
export function pixelsToWorld(camera: Camera, pixels: number): number {
  return pixels / camera.scale;
}
